import { Breakpoint, useTheme } from '@mui/material/styles';
import { useEffect, useState } from 'react';

export const defaultSpacingPresets = {
  sp: {
    s80: 8,
    s75: 7.5,
    s70: 7,
    s60: 6,
    s50: 5,
    s40: 4,
    s30: 3,
    s25: 2.5,
    s20: 2,
    s15: 1.5,
    s10: 1,
    s5: 0.5,
  },
  uiElems: {
    header: {
      mobile: {
        height: 42,
      },
      tablet: {
        height: 50,
      },
      desktop: {
        height: 72,
      },
    },
    speedStat: {
      mobile: {
        height: 21,
      },
      tablet: {
        height: 32,
      },
      desktop: {
        height: 42,
      },
    },
  },
  icons: {
    '9x12': {
      w: 9,
      h: 12,
    },
    '14x17': {
      w: 14,
      h: 17,
    },
    '16x16': {
      w: 16,
      h: 16,
    },
    '20x20': {
      w: 20,
      h: 20,
    },
    '32x32': {
      w: 32,
      h: 32,
    },
    '40x40': {
      w: 40,
      h: 40,
    },
    '60x50': {
      w: 60,
      h: 50,
    },
  },
};

export type IconsSizes = keyof (typeof defaultSpacingPresets)['icons'];

const generateProportionalPreset = (proportion: number) => {
  return {
    sp: {
      s80: Math.round(defaultSpacingPresets.sp.s80 / proportion),
      s75: Math.round(defaultSpacingPresets.sp.s75 / proportion),
      s70: Math.round(defaultSpacingPresets.sp.s70 / proportion),
      s60: Math.round(defaultSpacingPresets.sp.s60 / proportion),
      s50: Math.round(defaultSpacingPresets.sp.s50 / proportion),
      s40: Math.round(defaultSpacingPresets.sp.s40 / proportion),
      s30: Math.round(defaultSpacingPresets.sp.s30 / proportion),
      s25: Math.round(defaultSpacingPresets.sp.s25 / proportion),
      s20: Math.round(defaultSpacingPresets.sp.s20 / proportion),
      s15: Math.round(defaultSpacingPresets.sp.s15 / proportion),
      s10: Math.round(defaultSpacingPresets.sp.s10 / proportion),
      s5: Math.round(defaultSpacingPresets.sp.s5 / proportion),
    },
    uiElems: {
      header: {
        mobile: {
          height: Math.round(
            defaultSpacingPresets.uiElems.header.mobile.height / proportion
          ),
        },
        tablet: {
          height: Math.round(
            defaultSpacingPresets.uiElems.header.tablet.height / proportion
          ),
        },
        desktop: {
          height: Math.round(
            defaultSpacingPresets.uiElems.header.desktop.height / proportion
          ),
        },
      },
      speedStat: {
        mobile: {
          height: Math.round(
            defaultSpacingPresets.uiElems.speedStat.mobile.height / proportion
          ),
        },
        tablet: {
          height: Math.round(
            defaultSpacingPresets.uiElems.speedStat.tablet.height / proportion
          ),
        },
        desktop: {
          height: Math.round(
            defaultSpacingPresets.uiElems.speedStat.desktop.height / proportion
          ),
        },
      },
    },
    icons: {
      '9x12': {
        w: Math.round(defaultSpacingPresets.icons['9x12'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['9x12'].h / proportion),
      },
      '14x17': {
        w: Math.round(defaultSpacingPresets.icons['14x17'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['14x17'].h / proportion),
      },
      '16x16': {
        w: Math.round(defaultSpacingPresets.icons['16x16'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['16x16'].h / proportion),
      },
      '20x20': {
        w: Math.round(defaultSpacingPresets.icons['20x20'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['20x20'].h / proportion),
      },
      '32x32': {
        w: Math.round(defaultSpacingPresets.icons['32x32'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['32x32'].h / proportion),
      },
      '40x40': {
        w: Math.round(defaultSpacingPresets.icons['40x40'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['40x40'].h / proportion),
      },
      '60x50': {
        w: Math.round(defaultSpacingPresets.icons['60x50'].w / proportion),
        h: Math.round(defaultSpacingPresets.icons['60x50'].h / proportion),
      },
    },
  };
};

// Отступы используемые повсеместно, которые должны быть
// Одинаковыми во всех компонентах и зависят от текущего размера экрана
export const useGetSpacing = ({ currentBp }: { currentBp: Breakpoint }) => {
  const theme = useTheme();

  const [proportions] = useState({
    desktop_large:
      theme.breakpoints.values.desktop_middle /
      theme.breakpoints.values.desktop_large,
    desktop_middle: 1,
    desktop_small:
      theme.breakpoints.values.desktop_middle /
      theme.breakpoints.values.desktop_small,
    laptop:
      theme.breakpoints.values.desktop_small / theme.breakpoints.values.laptop,
  });

  const [largePreset] = useState(
    generateProportionalPreset(proportions['desktop_large'])
  );
  const [smallPreset] = useState(
    generateProportionalPreset(proportions['desktop_small'])
  );
  const [laptopPreset] = useState(
    generateProportionalPreset(proportions['laptop'])
  );

  const [spacingPreset, setSpacingPreset] = useState(defaultSpacingPresets);

  useEffect(() => {
    switch (currentBp) {
      case 'desktop_large':
        setSpacingPreset(largePreset);
        break;
      case 'desktop_middle':
        setSpacingPreset(defaultSpacingPresets);
        break;
      case 'desktop_small':
        setSpacingPreset(smallPreset);
        break;
      case 'laptop':
        setSpacingPreset(laptopPreset);
        break;
      default:
        setSpacingPreset(defaultSpacingPresets);
        break;
    }
  }, [currentBp, theme, laptopPreset, smallPreset, largePreset]);

  return spacingPreset;
};
